import { makeAutoObservable } from "mobx";
import ApiStore from "./ApiStore";
import ApplicationStore from "./ApplicationStore";
import { Employee } from "../models/Employee";
import { TypeTestQuestion } from "../enums/TypeTestQuestion";


export interface Test {
  id: number,
  name: string | null,
  level: string | null,
  description: string | null,
  numberOfQuestions: number,
  questionsToProvideCount: number,
  skillId?: number | null,
  companyId?: number | null,
  language: string | null,
  isPublic: boolean,
  isActive: boolean,
  isDeleted: boolean,
  numberOfAttempts: number,
  maxTimeMinutes: number,
  testQuestions: TestQuestion[]
}

export interface TestLevelSetting {
  id: number,
  level: string,
  minNumberOfQuestons: number,
  minNumberAttempts: number,
  minTime: number,
  minNumberOfGenerationQuestions: number,
  singleChoicePercentage: number,
  multipleChoicePercentage: number,
  writeCodePercentage: number,
  fixCodePercentage: number,
  openAnswerPercentage: number,
}

export interface TestPointSetting {
  id: number,
  questionTypeName: string,
  defaultPoints: number,
}

export interface TestQuestion {
  id: number,
  name: string,
  language: string,
  numberOfQuestion: number,
  correctAnswer?: string | null,
  code?: string | null,
  prompt?: string | null,
  testId: number,
  numberPoints: number,
  typeTestQuestionId: TypeTestQuestion,
  testOptions: TestOption[]
}

export interface TestOption {
  id: number,
  name: string,
  isCorrect: boolean,
  testQuestionId: number
}

export interface TestAssignmentStatus {
  id: number,
  name: string,
}

export interface TestAnswer {
  id: number,
  testQuestionId?: number | null,
  testAttemptId: number,
  isCorrect: boolean,
  questionName: string,
  answerTextName: string,
  numberPoints: number,
  correctAnswer?: string | null,
  answer: string
}

export interface UserTestAssignment {
  id: number,
  assignedDate: string,
  language: string | null,
  finishAssignedDate: string,
  startAssignedDate: string,
  testId: number,
  numberOfAttempts: number,
  test: Test | null,
  assignmentStatusId: number,
  assignmentStatus: TestAssignmentStatus | null,
  assignedByUserId: number,
  assignedToUserId: number,
  assignedToUser: Employee | null,
  testAttempts: TestAttempt[]
}

export interface TestAttempt {
  id: number,
  numberAttempt: number,
  percentage: number,
  startDate: string,
  finishDate: string,
  timeSpentMinutes: number | null,
  userTestAssignmentId: number | null,
  testId: number,
  companyUserId: number,
  testAnswers: TestAnswer[]
}

export interface AddGeneralAssignment {
  companyId: number,
  assignedByUserId: number,
  finishAssignedDate?: string | null,
  tests: Test[],
  includedUsers: number[],
  roleArray: number[],
}

class TestsStore {
  isLoading: boolean = true;
  isInitialized: boolean = false;
  ApiStore!: ApiStore;
  applicationStore!: ApplicationStore;
  testsPublic: Test[] = [];
  testsCompany: Test[] = [];
  testCompanyAssignments: UserTestAssignment[] = [];
  testUserAssignments: UserTestAssignment[] = [];
  levelSettings: TestLevelSetting[] = [];
  pointSettings: TestPointSetting[] = [];

  constructor(apiStore: ApiStore, applicationStore: ApplicationStore) {
    makeAutoObservable(this);
    this.ApiStore = apiStore;
    this.applicationStore = applicationStore;
  }

  initializeStore = async () => {
    if (this.isInitialized) {
      return;
    }

    this.setLoading(true);

    try {
      await this.getAllCompanyTestsWithoutQuestions(this.applicationStore.user.companyId.toString());
      await this.getAllAssignmentByCompanyId(this.applicationStore.company.Id ?? "0");
      await this.getAllPublicTestsWithoutQuestions();
      await this.getAllTestLevelSettings();
      await this.getAllTestPointSettings();

      this.isInitialized = true;
    } catch (error) {
      console.log("Error initialize test store: ", error);
    } finally {
      this.setLoading(false);
    }
  };

  setLoading = (loading: boolean) => {
    this.isLoading = loading;
  };

  createNew = async (addTest: Test, overwrite: boolean = false) => {
    this.isLoading = true;

    const createdTest = await this.ApiStore.TestsApiClient.addTest(addTest, overwrite);

    if (createdTest) {
      if (overwrite) {
        var existTest = this.testsCompany.find(t => t.skillId == createdTest.skillId && t.level == createdTest.level);

        const index = this.testsCompany.findIndex(assignment => assignment.id === existTest?.id);
        if (index !== -1) {
          this.testsCompany[index] = createdTest;
        }
      }
      else {
        this.testsCompany.push(createdTest);
      }
    }

    this.isLoading = false;

    return createdTest;
  };

  addAssignment = async (addAssignment: UserTestAssignment) => {
    this.isLoading = true;

    const createdAssignment = await this.ApiStore.TestsApiClient.addUserTestAssignment(addAssignment);

    if (createdAssignment) {
      this.testCompanyAssignments.push(createdAssignment);
    }

    this.isLoading = false;

    return createdAssignment;
  };

  addGeneralAssignments = async (generalAssignment: AddGeneralAssignment) => {
    this.isLoading = true;

    const response = await this.ApiStore.TestsApiClient.addGeneralUserTestAssignment(generalAssignment);
    this.isLoading = false;

    return response;
  };

  addAssignmentsPlan = async (addAssignmentsPlan: UserTestAssignment[], userId: number, overwrite: boolean = false) => {
    this.isLoading = true;

    console.log("overwrite", overwrite);
    const response = await this.ApiStore.TestsApiClient.addUserTestAssignmentPlan(addAssignmentsPlan, userId, overwrite);
    this.isLoading = false;

    return response;
  };

  deleteAssignmentById = async (assignmentId: string) => {
    this.isLoading = true;

    const result = await this.ApiStore.TestsApiClient.deleteAssignmentById(assignmentId).finally(() => {
      this.isLoading = false;
    });

    if (result) {
      this.testCompanyAssignments = this.testCompanyAssignments.filter(assignment => assignment.id !== parseInt(assignmentId, 10));
      this.testUserAssignments = this.testUserAssignments.filter(assignment => assignment.id !== parseInt(assignmentId, 10));
    }

    return result;
  };

  editUserTestAssignment = async (id: number, userTestAssignment: UserTestAssignment) => {
    this.isLoading = true;

    const updatedTestAssignment = await this.ApiStore.TestsApiClient.updateUserTestAssignment(id, userTestAssignment);

    if (updatedTestAssignment) {
      const index = this.testCompanyAssignments.findIndex(assignment => assignment.id === id);
      if (index !== -1) {
        this.testCompanyAssignments[index] = updatedTestAssignment;
      }
    }

    this.isLoading = false;
    return updatedTestAssignment;
  };

  updateUserTestAssignmentsPlan = async (userTestAssignmentsPlan: UserTestAssignment[], userId: number) => {
    this.isLoading = true;

    const result = await this.ApiStore.TestsApiClient.updateUserTestAssignmentPlan(userTestAssignmentsPlan, userId);

    this.isLoading = false;
    return result;
  };

  getAllTestLevelSettings = async () => {
    const allSettings = await this.ApiStore.TestsApiClient.getAllTestLevelSettings();

    if (allSettings != null && Array.isArray(allSettings)) {
      this.levelSettings = allSettings;
    }

    return allSettings;
  }

  getAllTestPointSettings = async () => {
    const allPointSettings = await this.ApiStore.TestsApiClient.getAllTestPointSettings();

    if (allPointSettings != null && Array.isArray(allPointSettings)) {
      this.pointSettings = allPointSettings;
    }

    return allPointSettings;
  }

  getAssignmentById = async (id: string) => {
    this.isLoading = true;
    const assignmentExist = await this.ApiStore.TestsApiClient.getAssignmentById(id);
    this.isLoading = false;

    return assignmentExist;
  }

  getTestAttemptByIdIncludeAll = async (id: string) => {
    this.isLoading = true;
    const testExist = await this.ApiStore.TestAttemptApiClient.getByIdIncludeAll(id);
    this.isLoading = false;

    return testExist;
  }

  updateTestAttempt = async (id: number, testAttempt: TestAttempt) => {
    this.isLoading = true;

    const result = await this.ApiStore.TestAttemptApiClient.updateTestAttempt(id, testAttempt);

    if (result.userTestAssignmentId != null) {
      const existUserTestAssignment = this.testCompanyAssignments.find(ta => ta.id === result.userTestAssignmentId);

      if (existUserTestAssignment) {
        existUserTestAssignment.testAttempts = existUserTestAssignment.testAttempts.map(ta =>
          ta.id === result.id ? result : ta
        );
      }
    }

    this.isLoading = false;
    return result;
  };

  updateTest = async (id: number, updateTest: Test) => {
    this.isLoading = true;

    const updatedTest = await this.ApiStore.TestsApiClient.updateTest(id, updateTest);

    if (updatedTest) {
      const targetArray = updateTest.companyId ? this.testsCompany : this.testsPublic;
      const index = targetArray.findIndex(test => test.id === id);
      if (index !== -1) {
        targetArray[index] = updatedTest;
      }
    }

    this.isLoading = false;
    return updatedTest;
  };

  getTestById = async (id: string) => {
    this.isLoading = true;
    const testExist = await this.ApiStore.TestsApiClient.getTestById(id);
    this.isLoading = false;

    return testExist;
  }

  addTestAttempt = async (addTestAttempt: TestAttempt) => {
    this.isLoading = true;

    const response = await this.ApiStore.TestAttemptApiClient.addTestAttempt(addTestAttempt);
    this.isLoading = false;

    return response;
  };

  getAllTestAttemptsByUserId = async (userId: string) => {
    this.isLoading = true;

    const testAttempts = await this.ApiStore.TestAttemptApiClient.getAllByUserId(userId).catch(() => {
      this.isLoading = false;
      return [];
    });

    this.isLoading = false;

    return testAttempts;
  };

  getAllAssignmentByUserId = async (userId: string) => {
    this.isLoading = true;

    const testAssignments = await this.ApiStore.TestsApiClient.getAllAssignmentByUserId(userId).catch(() => {
      this.isLoading = false;
      return [];
    });

    if (testAssignments) {
      this.testUserAssignments = testAssignments;
    }

    this.isLoading = false;

    return testAssignments;
  }

  getAllAssignmentByCompanyId = async (companyId: string) => {
    this.isLoading = true;
    const testAssignments = await this.ApiStore.TestsApiClient.getAllAssignmentByCompanyId(companyId).catch(() => {
      this.isLoading = false;
      return [];
    });
    this.isLoading = false;
    if (testAssignments) {
      this.testCompanyAssignments = testAssignments;
    }
    return testAssignments;
  }

  deleteById = async (id: string) => {
    this.isLoading = true;
    const result = await this.ApiStore.TestsApiClient.deleteById(id).finally(() => {
      this.isLoading = false;
    });

    if (result) {
      this.testsCompany = this.testsCompany.filter(test => test.id !== parseInt(id, 10));
      this.testsPublic = this.testsPublic.filter(test => test.id !== parseInt(id, 10));
    }

    return result;
  };

  getAllPublicTests = async () => {
    this.isLoading = true;
    const testsPublic = await this.ApiStore.TestsApiClient.getAllPublic().catch(() => {
      this.isLoading = false;
      return [];
    });
    this.isLoading = false;
    if (testsPublic) {
      this.testsPublic = testsPublic;
    }
    return testsPublic;
  };

  getAllPublicTestsWithoutQuestions = async () => {
    this.isLoading = true;
    const testsPublic = await this.ApiStore.TestsApiClient.getAllPublicWithoutQuestions().catch(() => {
      this.isLoading = false;
      return [];
    });
    this.isLoading = false;
    if (testsPublic) {
      this.testsPublic = testsPublic;
    }
    return testsPublic;
  };

  getAllCompanyTests = async (companyId: string) => {
    this.isLoading = true;
    const testsCompany = await this.ApiStore.TestsApiClient.getAllByCompanyId(companyId).catch(() => {
      this.isLoading = false;
      return [];
    });
    this.isLoading = false;
    if (testsCompany) {
      this.testsCompany = testsCompany;
    }
    return testsCompany;
  }

  getAllCompanyTestsWithoutQuestions = async (companyId: string) => {
    this.isLoading = true;
    const testsCompany = await this.ApiStore.TestsApiClient.getAllByCompanyIdWithoutQuestions(companyId).catch(() => {
      this.isLoading = false;
      return [];
    });
    this.isLoading = false;
    if (testsCompany) {
      this.testsCompany = testsCompany;
    }
    return testsCompany;
  }
}
export default TestsStore;
